// src/belediye/services/belediyeApi.js
// (Multi-unit + refresh token destekli, backward compatible)

import axios from "axios";
import { toast } from "react-toastify";

import { readUiOnFromStorage, readAuthzFromStorage } from "../utils/authzStorage";
import { isPoolHeaderSuppressed } from "../utils/poolHeader";
// FAZ-105 — resmî belge indirmede Content-Disposition'dan dosya adı okumak için
import { pickFilenameFromContentDisposition } from "../components/utils/downloadFile";

// ------------------------------------------------------------
// Base URL
// ------------------------------------------------------------
const RAW_API_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api";
const API_URL = String(RAW_API_URL).replace(/\/+$/, "");

if (!API_URL) {
  throw new Error(
    "Çevresel değişken eksik! REACT_APP_BACKEND_URL kontrol ediniz."
  );
}

const belediyeApi = axios.create({
  baseURL: API_URL,
});

// ------------------------------------------------------------
// LocalStorage keys
// ------------------------------------------------------------
const LS = {
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  activeUnitSlug: "activeUnitSlug",
};

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------

// undefined değerleri query'den temizle
const stripUndefined = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  );
};

// ------------------------------------------------------------
// Helpers (Applications list normalize)
// ------------------------------------------------------------
const normalizePaginated = (data) => {
  if (Array.isArray(data?.results)) {
    return {
      items: data.results,
      count: Number(data.count ?? data.results.length ?? 0),
      next: data.next ?? null,
      previous: data.previous ?? null,
    };
  }
  if (Array.isArray(data)) {
    return { items: data, count: data.length, next: null, previous: null };
  }
  return { items: [], count: 0, next: null, previous: null };
};

const DEFAULT_APPLICATIONS_PAGE_SIZE = 50;
const DEFAULT_APPLICATIONS_ORDERING = "-created_at";

/* ============================================================
 *  Global Fullscreen Loading Event System
 *  - Opt-in çalışır
 *  - Her request otomatik fullscreen açmaz
 * ============================================================ */

export const BELEDIYE_GLOBAL_LOADING_EVENT = "belediye:global-loading";

const GLOBAL_LOADING_DELAY_MS = 180;
const GLOBAL_LOADING_MIN_VISIBLE_MS = 320;

let globalLoadingTrackedCount = 0;
let globalLoadingVisible = false;
let globalLoadingShownAt = 0;
let globalLoadingShowTimer = null;
let globalLoadingHideTimer = null;

let globalLoadingLastPayload = {
  title: "Yükleniyor",
  subtitle: "Lütfen bekleyiniz...",
};

const emitGlobalLoading = (detail) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(BELEDIYE_GLOBAL_LOADING_EVENT, {
      detail,
    })
  );
};

const readLoaderMeta = (config) => {
  const loader = config?.meta?.loader || {};

  return {
    enabled: loader.enabled === true,
    title: loader.title || "Yükleniyor",
    subtitle: loader.subtitle || "Lütfen bekleyiniz...",
  };
};

const beginGlobalLoading = (config) => {
  const loader = readLoaderMeta(config);

  if (!loader.enabled) return;
  if (config.__globalLoaderTracked) return;

  config.__globalLoaderTracked = true;
  config.__globalLoaderSettled = false;

  globalLoadingTrackedCount += 1;
  globalLoadingLastPayload = {
    title: loader.title,
    subtitle: loader.subtitle,
  };

  if (globalLoadingHideTimer) {
    clearTimeout(globalLoadingHideTimer);
    globalLoadingHideTimer = null;
  }

  if (globalLoadingVisible) return;
  if (globalLoadingShowTimer) return;

  globalLoadingShowTimer = window.setTimeout(() => {
    globalLoadingShowTimer = null;

    if (globalLoadingTrackedCount <= 0 || globalLoadingVisible) return;

    globalLoadingVisible = true;
    globalLoadingShownAt = Date.now();

    emitGlobalLoading({
      visible: true,
      title: globalLoadingLastPayload.title,
      subtitle: globalLoadingLastPayload.subtitle,
    });
  }, GLOBAL_LOADING_DELAY_MS);
};

const endGlobalLoading = (config) => {
  if (!config?.__globalLoaderTracked) return;
  if (config.__globalLoaderSettled) return;

  config.__globalLoaderSettled = true;
  globalLoadingTrackedCount = Math.max(0, globalLoadingTrackedCount - 1);

  if (globalLoadingTrackedCount > 0) return;

  if (globalLoadingShowTimer) {
    clearTimeout(globalLoadingShowTimer);
    globalLoadingShowTimer = null;
    return;
  }

  if (!globalLoadingVisible) return;

  const elapsed = Date.now() - globalLoadingShownAt;
  const remaining = Math.max(0, GLOBAL_LOADING_MIN_VISIBLE_MS - elapsed);

  globalLoadingHideTimer = window.setTimeout(() => {
    globalLoadingHideTimer = null;
    globalLoadingVisible = false;

    emitGlobalLoading({
      visible: false,
    });
  }, remaining);
};

/* ------------------------------------------------------------
 * Request config helpers
 * ------------------------------------------------------------ */

export const withFullscreenLoader = (
  loader = {},
  requestConfig = {}
) => ({
  ...requestConfig,
  meta: {
    ...(requestConfig.meta || {}),
    loader: {
      enabled: true,
      title: loader.title || "Yükleniyor",
      subtitle: loader.subtitle || "Lütfen bekleyiniz...",
    },
  },
});

export const withoutGlobalLoader = (requestConfig = {}) => ({
  ...requestConfig,
  meta: {
    ...(requestConfig.meta || {}),
    loader: {
      ...((requestConfig.meta || {}).loader || {}),
      enabled: false,
    },
  },
});

// Token helpers
const getAccessToken = () => localStorage.getItem(LS.accessToken);
const getRefreshToken = () => localStorage.getItem(LS.refreshToken);
const setAccessToken = (token) => localStorage.setItem(LS.accessToken, token);
const clearSession = () => {
  localStorage.removeItem(LS.accessToken);
  localStorage.removeItem(LS.refreshToken);
  localStorage.removeItem("user");
  localStorage.removeItem(LS.activeUnitSlug);
};

// /yonetim/:unitSlug/... -> unitSlug
const getUnitSlugFromRoute = () => {
  try {
    const path = window.location?.pathname || "";
    const m = path.match(/^\/yonetim\/([^/]+)/);
    if (!m) return null;
    const slug = decodeURIComponent(m[1]);
    return slug || null;
  } catch {
    return null;
  }
};

const isBaskanlikRoute = (slug) => slug === "baskanlik";

// Unit olmayan (reserved) route slug'ları: X-Unit-Slug asla gönderilmemeli.
// FAZ-22 (denetim fix): shell + canonical route literalleri de dahil — canonical
// rotada (/yonetim/basvurular vb.) route slug'ı birim sanılıp header'a düşmesin.
// utils/activeUnit.js RESERVED ile hizalı (+baskanlik isBaskanlikRoute'ta da var).
const RESERVED_ROUTE_SLUGS = new Set([
  "birim-sec", "halk-masasi", "sistem", "saha", "baskanlik",
  "dashboard", "basvurular", "raporlar", "personel", "bildirimler",
]);

const isHalkMasasiPath = () => {
  try {
    const path = window.location?.pathname || "";
    return path.startsWith("/yonetim/halk-masasi");
  } catch {
    return false;
  }
};

// Önce localStorage'daki aktif birim, sonra route fallback
const readUiProfileFromStorage = () => {
  try {
    return readAuthzFromStorage()?.uiProfile ?? null;
  } catch {
    return null;
  }
};

const resolveUnitSlugForHeader = () => {
  if (isHalkMasasiPath()) return null;

  const active = localStorage.getItem(LS.activeUnitSlug);

  // FAZ-25 HAVUZ: permission_ui (uiOn) AÇIK + ui_profile=yonetici + AÇIK birim
  // seçimi YOK → X-Unit-Slug GÖNDERME → backend permission-aware havuzu
  // (allowed_unit_ids_for_action) hesaplar. uiOn KAPALIYKEN (production pilot
  // kapalı) ASLA bastırma → bugünkü aktif-birim davranışı (dormant).
  if (isPoolHeaderSuppressed(readUiOnFromStorage(), readUiProfileFromStorage(), active)) {
    return null;
  }

  if (
    active &&
    !RESERVED_ROUTE_SLUGS.has(active) &&
    !isBaskanlikRoute(active)
  ) {
    return active;
  }

  const routeSlug = getUnitSlugFromRoute();
  if (!routeSlug) return null;
  if (RESERVED_ROUTE_SLUGS.has(routeSlug)) return null;
  if (isBaskanlikRoute(routeSlug)) return null;

  return routeSlug;
};

const isAuthEndpoint = (url) => {
  const u = String(url || "");
  return u.includes("/token/") || u.endsWith("/token") || u.includes("/auth/");
};

// FAZ-28: test edilebilirlik için export (gorev.nav.test — muafiyet sözleşmesi).
export const isNoUnitHeaderEndpoint = (url) => {
  const u = String(url || "");
  return (
    u.includes("/common/users/me/") ||
    u.includes("/common/users/my-units/") ||
    u.includes("/common/users/validate-unit/") ||
    u.includes("/common/users/assignable-by-unit/") ||
    // FAZ-15 Sistem Yönetimi — GLOBAL (birim bağımsız) yönetim uçları
    u.includes("/common/roles") ||
    u.includes("/common/unit-memberships") ||
    u.includes("/common/sistem-units") ||
    u.includes("/common/feature-actions") ||
    u.includes("/effective-permissions") ||
    // FAZ-24 yeni yönetim ekranları — GLOBAL (birim bağımsız): mahalle referans
    // verisi, KVKK vatandaş yönetimi, idari sınırlar. X-Unit-Slug iliştirilmemeli.
    u.includes("/core/neighborhoods") ||
    u.includes("/core/citizens-admin") ||
    u.includes("/bm4/boundaries") ||
    // FAZ-28 Kurum İçi Görevler — görünürlük KİMLİKTEN (07 §3 kuralı): kişiye
    // atanmış görev HER çalışma alanında görünür; X-Unit-Slug ASLA iliştirilmez.
    u.includes("/internal-tasks/") ||
    // Mesajlaşma — sohbetler de KİMLİKTEN (katılımcılık); birim başlığı anlamsız.
    u.includes("/mesajlasma/")
  );
};

// ------------------------------------------------------------
// Request interceptor: Authorization + Active Unit
// ------------------------------------------------------------
belediyeApi.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    const halkMasasi = isHalkMasasiPath();

    if (halkMasasi) {
      if (config?.headers && config.headers["X-Unit-Slug"]) {
        delete config.headers["X-Unit-Slug"];
      }
    } else {
      const unitSlug = resolveUnitSlugForHeader();

      if (
        unitSlug &&
        !isAuthEndpoint(config?.url) &&
        !isNoUnitHeaderEndpoint(config?.url)
      ) {
        config.headers = config.headers || {};

        if (!config.headers["X-Unit-Slug"]) {
          config.headers["X-Unit-Slug"] = unitSlug;
        }
      }
    }

    beginGlobalLoading(config);
    return config;
  },
  (error) => Promise.reject(error)
);

// ------------------------------------------------------------
// Response interceptor: refresh token ile 401 retry
// ------------------------------------------------------------
let isRefreshing = false;
let refreshQueue = [];

const processRefreshQueue = (error, newToken = null) => {
  refreshQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(newToken);
  });
  refreshQueue = [];
};

belediyeApi.interceptors.response.use(
  (response) => {
    endGlobalLoading(response?.config);
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;

    if (!error?.response) {
      endGlobalLoading(originalRequest);
      return Promise.reject(error);
    }

    const status = error.response.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const refresh = getRefreshToken();
      if (!refresh) {
        endGlobalLoading(originalRequest);
        clearSession();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return belediyeApi(originalRequest);
          })
          .catch((err) => {
            endGlobalLoading(originalRequest);
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_URL}/token/refresh/`, {
          refresh,
        });

        const newAccess = data?.access;
        if (!newAccess) throw new Error("Refresh response access token yok.");

        setAccessToken(newAccess);
        processRefreshQueue(null, newAccess);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${newAccess}`;

        return belediyeApi(originalRequest);
      } catch (refreshErr) {
        processRefreshQueue(refreshErr, null);
        endGlobalLoading(originalRequest);
        clearSession();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    // FAZ-12 (2.5): 403 interceptor'ı — YALNIZ permission_ui şalteri açıkken
    // (uiOn) devrede; uiOn=false iken bugünkü davranış (sessiz reject) AYNEN.
    if (status === 403 && readUiOnFromStorage()) {
      // "Bu işlem için yetkiniz yok" toast'ı SADECE kullanıcının BİZZAT
      // tetiklediği MUTASYON isteklerinde (POST/PUT/PATCH/DELETE) gösterilir.
      // Sayfa açılışındaki arka-plan GET probe'ları (kullanıcının bazı veriyi
      // görme yetkisi olmayabilir; bu normaldir) sessizce reddedilir → böylece
      // "gerekli gereksiz her sayfada yetkiniz yok" gürültüsü biter.
      // İstisnalar (isteğe özel config):
      //   authzToast === true  → GET olsa bile toast'ı ZORLA göster
      //   authzToast === false → mutasyon olsa bile toast'ı SUSTUR
      const method = String(originalRequest?.method || "get").toLowerCase();
      const isMutation =
        method === "post" ||
        method === "put" ||
        method === "patch" ||
        method === "delete";
      const forceToast = originalRequest?.authzToast === true;
      const silenceToast = originalRequest?.authzToast === false;

      if (!silenceToast && (forceToast || isMutation)) {
        toast.error("Bu işlem için yetkiniz yok.", { toastId: "authz-403" });
      }
      // Sayfa-kritik istekler (FAZ-13'te işaretlenecek): config.authzCritical
      if (originalRequest?.authzCritical === true) {
        window.location.assign("/yonetim/yetkisiz");
      }
    }

    endGlobalLoading(originalRequest);
    return Promise.reject(error);
  }
);

/* ============================================================
 *  Applications API (List + Search)
 * ============================================================ */

const APPLICATIONS_ENDPOINT = "/core/applications/";

export const fetchApplications = async (params = {}, requestConfig = {}) => {
  const { data } = await belediyeApi.get(APPLICATIONS_ENDPOINT, {
    ...requestConfig,
    params: stripUndefined(params),
  });
  return normalizePaginated(data);
};

export const fetchApplicationsByStatusPage = async (
  {
    status = "",
    page = 1,
    pageSize = DEFAULT_APPLICATIONS_PAGE_SIZE,
    q = "",
    ordering = DEFAULT_APPLICATIONS_ORDERING,
    ...rest
  } = {},
  requestConfig = {}
) => {
  return await fetchApplications(
    {
      ...rest,
      ...(status ? { status } : {}),
      ...(q ? { q: String(q).trim() } : {}),
      ordering,
      page,
      page_size: pageSize,
    },
    requestConfig
  );
};

export const fetchApplicationsByUnitPage = async (
  unitId,
  {
    status = "",
    page = 1,
    pageSize = DEFAULT_APPLICATIONS_PAGE_SIZE,
    q = "",
    ordering = DEFAULT_APPLICATIONS_ORDERING,
    ...rest
  } = {},
  requestConfig = {}
) => {
  if (!unitId) throw new Error("unitId zorunlu");

  const { data } = await belediyeApi.get(
    `/core/applications/unit/${unitId}/`,
    {
      ...requestConfig,
      params: stripUndefined({
        ...rest,
        ...(status ? { status } : {}),
        ...(q ? { q: String(q).trim() } : {}),
        ordering,
        page,
        page_size: pageSize,
      }),
    }
  );

  return normalizePaginated(data);
};

export const fetchApplicationDetail = async (
  applicationId,
  requestConfig = {}
) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  const { data } = await belediyeApi.get(
    `/core/applications/${applicationId}/`,
    requestConfig
  );

  return data;
};

export const fetchApplicationDetailBundle = async (
  applicationId,
  requestConfig = {}
) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  const { data } = await belediyeApi.get(
    `/core/applications/${applicationId}/detail-bundle/`,
    requestConfig
  );

  return data?.application ?? data;
};

/**
 * indirResmiBelge — FAZ-105: başvurunun resmî belge (PDF) çıktısı.
 *
 * İzin: applications.document.print (backend gateّli; 403 dönerse çağıran
 * kullanıcıya uygun mesaj gösterir).
 *
 * ÇİFT "/api" TUZAĞI: belediyeApi.baseURL zaten ".../api" ile bitiyor.
 * Bu yüzden yol "/core/..." olarak GÖRELİ verilir — mutlak URL kurup
 * "/api" eklemek daha önce mesajlaşma ve görev modüllerinde 404 üretmişti.
 *
 * Dönüş: { blob, dosyaAdi, belgeKimligi }
 */
export const acResmiBelgeOnizleme = async (applicationId) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  // Uç JWT ile korunuyor → yeni sekmeye düz URL veremeyiz (tarayıcı Authorization
  // başlığı göndermez). HTML'i yetkili istekle alıp blob URL olarak açıyoruz.
  const response = await belediyeApi.get(
    `/core/applications/${applicationId}/resmi-belge/`,
    { params: { bicim: "html" }, responseType: "blob" }
  );

  return {
    blob: response.data,
    belgeKimligi: response?.headers?.["x-belge-kimligi"] || "",
  };
};

export const indirResmiBelge = async (applicationId) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  const response = await belediyeApi.get(
    `/core/applications/${applicationId}/resmi-belge/`,
    { responseType: "blob" }
  );

  const cd =
    response?.headers?.["content-disposition"] ||
    response?.headers?.["Content-Disposition"];

  return {
    blob: response.data,
    dosyaAdi: pickFilenameFromContentDisposition(cd) || "basvuru-durum-raporu.pdf",
    belgeKimligi:
      response?.headers?.["x-belge-kimligi"] ||
      response?.headers?.["X-Belge-Kimligi"] ||
      "",
  };
};

/**
 * recordApplicationRead — FAZ-54 "okundu" kaydı.
 *
 * Detay sayfasında 5 saniyeden fazla kalındığında çağrılır. Yan işlevdir:
 * başarısız olursa kullanıcıya HİÇBİR hata gösterilmez, sayfa akışı bozulmaz.
 * Sunucu 204 döner (istemcinin veriye ihtiyacı yok).
 */
export const recordApplicationRead = async (applicationId, requestConfig = {}) => {
  if (!applicationId) return;
  await belediyeApi.post(
    "/staff/application-reads/",
    { application: applicationId },
    requestConfig
  );
};

/**
 * fetchApplicationStatusCounts
 *
 * Aktif filtre + scope altında statü başına başvuru sayısı döndürür.
 * Sekme rozetlerini beslemek için hafif aggregate uç — 50'lik liste çekmez.
 * `status` param'ı GÖNDERİLMEZ (tüm statüler sayılır).
 *
 * @returns {{ yeni, işleme_alındı, yönlendirildi, çözüldü, çözülemedi, total }}
 */
export const fetchApplicationStatusCounts = async (
  params = {},
  requestConfig = {}
) => {
  const { status, ...rest } = params || {};
  const { data } = await belediyeApi.get(
    `${APPLICATIONS_ENDPOINT}status-counts/`,
    {
      ...requestConfig,
      params: stripUndefined(rest),
    }
  );
  return data && typeof data === "object" ? data : {};
};

export const searchApplicationsQuick = async ({
  q,
  limit = 20,
  requestConfig = {},
  ...rest
} = {}) => {
  if (!q || !String(q).trim()) {
    return { items: [], count: 0, next: null, previous: null };
  }

  return await fetchApplicationsByStatusPage(
    {
      ...rest,
      q: String(q).trim(),
      page: 1,
      pageSize: limit,
      ordering: DEFAULT_APPLICATIONS_ORDERING,
    },
    requestConfig
  );
};

/* ============================================================
 *  Units & Neighborhoods (Filtre dropdown besleme)
 *  - /core/units/ ve /core/neighborhoods/ AllowAny + salt-okunur.
 *  - Pagination yok; düz liste döner (normalizePaginated ikisini de karşılar).
 * ============================================================ */

export const fetchUnits = async (requestConfig = {}) => {
  const { data } = await belediyeApi.get("/core/units/", requestConfig);
  const { items } = normalizePaginated(data);
  return items.map((u) => ({
    id: u?.id ?? null,
    name: u?.name ?? "",
    slug: u?.slug ?? "",
  }));
};

export const fetchNeighborhoods = async (requestConfig = {}) => {
  const { data } = await belediyeApi.get("/core/neighborhoods/", requestConfig);
  const { items } = normalizePaginated(data);
  return items.map((n) => ({
    id: n?.id ?? null,
    name: n?.name ?? "",
  }));
};

/* ============================================================
 *  Personnel Panel API
 * ============================================================ */

export const fetchPersonnelPanel = async (
  applicationId,
  { history = false } = {},
  requestConfig = {}
) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  const { data } = await belediyeApi.get(
    `/staff/assignments/personnel-panel/`,
    {
      ...requestConfig,
      params: stripUndefined({
        application: applicationId,
        ...(history ? { history: 1 } : {}),
      }),
    }
  );

  return data;
};

export const fetchAssignableUsersByUnit = async (
  unitId,
  requestConfig = {}
) => {
  if (!unitId) throw new Error("unitId zorunlu");

  const { data } = await belediyeApi.get(
    `/common/users/assignable-by-unit/${unitId}/`,
    requestConfig
  );

  const list = Array.isArray(data) ? data : [];

  return list.map((user) => ({
  id: user?.id ?? null,
  first_name: user?.first_name ?? "",
  last_name: user?.last_name ?? "",
  full_name:
    user?.full_name ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
  username: user?.username ?? "",
  role_in_unit: user?.role_in_unit ?? "",
  effective_role_in_unit: user?.effective_role_in_unit ?? "",
  unit_id: user?.unit_id ?? null,
  unit_slug: user?.unit_slug ?? "",
  source: user?.source ?? "",
  is_assignable: user?.is_assignable === true,

  // YENİ: PersonnelStep görünürlük / aktiflik alanları
  user_is_active: user?.user_is_active !== false,
  membership_is_active:
    user?.membership_is_active === null || user?.membership_is_active === undefined
      ? null
      : user?.membership_is_active === true,
  personnel_step_visible: user?.personnel_step_visible !== false,
  inactive_reason: user?.inactive_reason ?? "",
}));
};

/* ============================================================
 *  Survey Reports API (Başkan)
 * ============================================================ */

export const fetchSurveyReportSummary = async (params) => {
  const { data } = await belediyeApi.get("/surveys/surveys/report-summary/", {
    params: stripUndefined(params),
  });
  return data;
};

export const fetchSurveyReport = async (surveyId, params) => {
  if (!surveyId) throw new Error("surveyId zorunlu");
  const { data } = await belediyeApi.get(`/surveys/surveys/${surveyId}/report/`, {
    params: stripUndefined(params),
  });
  return data;
};

export const fetchSurveyReportTimeseries = async (surveyId, params) => {
  if (!surveyId) throw new Error("surveyId zorunlu");
  const { data } = await belediyeApi.get(
    `/surveys/surveys/${surveyId}/report-timeseries/`,
    { params: stripUndefined(params) }
  );
  return data;
};

export const downloadSurveyReportCsv = async (surveyId, params) => {
  if (!surveyId) throw new Error("surveyId zorunlu");
  const response = await belediyeApi.get(
    `/surveys/surveys/${surveyId}/report-export/`,
    {
      params: stripUndefined(params),
      responseType: "blob",
    }
  );
  return response;
};

/* ============================================================
 *  Backward compatible alias exports
 * ============================================================ */

const normalizeDateParams = (params = {}) => {
  const from = params.from ?? params.date_from;
  const to = params.to ?? params.date_to;

  const surveyId = params.surveyId ?? params.survey;

  const {
    from: _f,
    to: _t,
    date_from: _df,
    date_to: _dt,
    surveyId: _sid,
    survey: _s,
    ...rest
  } = params;

  const apiParams = {
    ...rest,
    ...(from ? { from, date_from: from } : {}),
    ...(to ? { to, date_to: to } : {}),
  };

  return { surveyId, apiParams: stripUndefined(apiParams) };
};

export const fetchSurveysWithStats = async (params) => {
  const { apiParams } = normalizeDateParams(params || {});
  const finalParams = Object.keys(apiParams || {}).length ? apiParams : undefined;
  return await fetchSurveyReportSummary(finalParams);
};

export const fetchSurveyStats = async (params) => {
  const { surveyId, apiParams } = normalizeDateParams(params);
  if (!surveyId) throw new Error("surveyId/survey zorunlu");
  return await fetchSurveyReport(surveyId, apiParams);
};

export const fetchSurveyTimeseries = async (params) => {
  const { surveyId, apiParams } = normalizeDateParams(params);
  if (!surveyId) throw new Error("surveyId/survey zorunlu");

  const finalParams = stripUndefined({
    ...apiParams,
    bucket: apiParams.bucket ?? apiParams.period,
    period: apiParams.period ?? apiParams.bucket,
  });

  return await fetchSurveyReportTimeseries(surveyId, finalParams);
};

export const downloadSurveyResponsesCsv = async (params) => {
  const { surveyId, apiParams } = normalizeDateParams(params);
  if (!surveyId) throw new Error("surveyId/survey zorunlu");
  return await downloadSurveyReportCsv(surveyId, apiParams);
};

/* ============================================================
 *  YENİ: Redirected Applications API
 *  Faz 2 — RedirectedApplicationsPage ekranı için
 *
 *  Mevcut hiçbir export'a dokunulmadı.
 *  İki yeni helper eklendi: liste ve timeline.
 * ============================================================ */

const FORWARD_LOGS_ENDPOINT = "/staff/forward-logs";

/**
 * fetchRedirectedApplicationsPage
 *
 * Ana liste endpoint'i. Başvuru bazlı özet satırlar döndürür.
 * Ham log döndürmez.
 *
 * @param {Object} params
 *   - page       {number}  Sayfa numarası (varsayılan: 1)
 *   - page_size  {number}  Sayfa boyutu (varsayılan: 20, max: 50)
 *   - unit       {number}  Birim id filtresi
 *   - scope      {string}  "all" | "incoming" | "outgoing"
 *   - q          {string}  Tracking number veya subject arama
 * @param {Object} requestConfig  axios config (withFullscreenLoader vb.)
 *
 * @returns {{ items, count, next, previous }}
 */
export const fetchRedirectedApplicationsPage = async (
  {
    page = 1,
    page_size = 20,
    unit,
    scope,
    q,
    ...rest
  } = {},
  requestConfig = {}
) => {
  const { data } = await belediyeApi.get(
    `${FORWARD_LOGS_ENDPOINT}/redirected-applications/`,
    {
      ...requestConfig,
      params: stripUndefined({
        ...rest,
        page,
        page_size,
        ...(unit   ? { unit }   : {}),
        ...(scope  ? { scope }  : {}),
        ...(q && String(q).trim() ? { q: String(q).trim() } : {}),
      }),
    }
  );

  return normalizePaginated(data);
};

/**
 * fetchRedirectedApplicationHistory
 *
 * Accordion açıldığında lazy olarak çağrılır.
 * Tek bir başvurunun yönlendirme timeline'ını döndürür.
 * Fullscreen loader KULLANILMAMALI — satır içi loading yeterli.
 *
 * @param {number|string} applicationId  Başvuru id'si
 * @param {Object}        requestConfig  axios config
 *
 * @returns {Array}  ForwardLogTimelineItem[]
 */
export const fetchRedirectedApplicationHistory = async (
  applicationId,
  requestConfig = {}
) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  const { data } = await belediyeApi.get(
    `${FORWARD_LOGS_ENDPOINT}/application-history/`,
    {
      ...requestConfig,
      params: { application: applicationId },
    }
  );

  return Array.isArray(data) ? data : [];
};

export default belediyeApi;